import { useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  fetchVehicleCountByMonth,
  fetchVehicleCountByHgStage,
} from "../../utils/api";

const TEAL = "#1e6b6b";

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  background: "white",
  border: "1px solid #dde3e8",
  borderRadius: 6,
  padding: 18,
  minHeight: 380,
};

const titleStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: TEAL,
  marginBottom: 14,
};

const bodyMessageStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  color: "#888",
  fontSize: 13,
  textAlign: "center",
  minHeight: 300,
};

const axisTick = { fontSize: 12, fill: "#555" };
const axisLine = { stroke: "#dde3e8" };
const tooltipProps = {
  cursor: { fill: "rgba(30,107,107,0.08)" },
  formatter: (value) => [value, "Vehicle Count"],
  contentStyle: { borderRadius: 6, border: "1px solid #dde3e8", fontSize: 12 },
};

/**
 * A single self-contained dashboard bar chart. It owns its data fetch and its
 * loading / error / empty state, so one chart failing never blocks the other.
 * Reuses the shared Athena API layer (src/vp/utils/api.js) and recharts.
 *
 * `horizontal` switches between the two visual styles:
 *   false (default) -> vertical bars: category on X, Vehicle Count on Y
 *   true            -> horizontal bars: category on Y, Vehicle Count on X
 * Both styles share the same MAN teal styling, typography and spacing.
 */
function DashboardBarChart({
  title,
  fetcher,
  categoryKey,
  categoryLabel,
  emptyMessage,
  horizontal = false,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);

  useEffect(() => {
    // Ref-guard only (no cleanup "cancelled" flag) so StrictMode's double-mount
    // cannot cancel the single in-flight request; finally() always clears loading.
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    setLoading(true);
    setError(null);
    fetcher()
      .then((rows) => setData(Array.isArray(rows) ? rows : []))
      .catch((err) =>
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Could not load chart data.",
        ),
      )
      .finally(() => setLoading(false));
  }, [fetcher]);

  const hasAnyData = data.some((d) => Number(d.vehicle_count) > 0);

  let body;
  if (loading) {
    body = (
      <div style={bodyMessageStyle}>
        <div
          style={{
            width: 28,
            height: 28,
            border: "3px solid #dde3e8",
            borderTopColor: TEAL,
            borderRadius: "50%",
            animation: "vp-dash-spin 0.8s linear infinite",
          }}
        />
        <span>Loading vehicle data...</span>
      </div>
    );
  } else if (error) {
    body = (
      <div style={bodyMessageStyle}>
        <span style={{ color: "#c0392b", fontWeight: 600 }}>
          Unable to load chart data
        </span>
        <span>{error}</span>
      </div>
    );
  } else if (!hasAnyData) {
    body = <div style={bodyMessageStyle}>{emptyMessage}</div>;
  } else {
    body = (
      <div style={{ flex: 1, minHeight: 320, width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          {horizontal ? (
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 10, right: 28, left: 12, bottom: 20 }}
              barCategoryGap="22%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#eef2f2"
                horizontal={false}
              />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={axisTick}
                tickLine={false}
                axisLine={axisLine}
                label={{
                  value: "Vehicle Count",
                  position: "insideBottom",
                  offset: -8,
                  fontSize: 12,
                  fill: "#555",
                }}
              />
              <YAxis
                type="category"
                dataKey={categoryKey}
                width={64}
                tick={axisTick}
                tickLine={false}
                axisLine={axisLine}
                label={{
                  value: categoryLabel,
                  angle: -90,
                  position: "insideLeft",
                  offset: 12,
                  fontSize: 12,
                  fill: "#555",
                  style: { textAnchor: "middle" },
                }}
              />
              <Tooltip {...tooltipProps} />
              <Bar
                dataKey="vehicle_count"
                name="Vehicle Count"
                fill={TEAL}
                radius={[0, 3, 3, 0]}
                maxBarSize={40}
              />
            </BarChart>
          ) : (
            <BarChart
              data={data}
              margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
              barCategoryGap="25%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#eef2f2"
                vertical={false}
              />
              <XAxis
                dataKey={categoryKey}
                tick={axisTick}
                tickLine={false}
                axisLine={axisLine}
                label={{
                  value: categoryLabel,
                  position: "insideBottom",
                  offset: -8,
                  fontSize: 12,
                  fill: "#555",
                }}
              />
              <YAxis
                allowDecimals={false}
                tick={axisTick}
                tickLine={false}
                axisLine={axisLine}
                label={{
                  value: "Vehicle Count",
                  angle: -90,
                  position: "insideLeft",
                  offset: 16,
                  fontSize: 12,
                  fill: "#555",
                  style: { textAnchor: "middle" },
                }}
              />
              <Tooltip {...tooltipProps} />
              <Bar
                dataKey="vehicle_count"
                name="Vehicle Count"
                fill={TEAL}
                radius={[3, 3, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>{title}</div>
      {body}
    </div>
  );
}

/**
 * Vehicle Planning Dashboard. Two visually distinct, independent charts:
 *  1) Vehicle Count by Month     -> vertical bars   (/vp/vehicle-count-by-month)
 *  2) Vehicle Count by HG Stage  -> horizontal bars (/vp/vehicle-count-by-hg-stage)
 * No hardcoded data; each chart pulls live Athena results and manages its own
 * loading/error state.
 */
export default function DashboardView() {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}
    >
      <DashboardBarChart
        title="Vehicle Count by Month"
        fetcher={fetchVehicleCountByMonth}
        categoryKey="month"
        categoryLabel="Month"
        emptyMessage="No completed-vehicle data available."
      />
      <DashboardBarChart
        title="Vehicle Count by HG Stage"
        fetcher={fetchVehicleCountByHgStage}
        categoryKey="hg_stage"
        categoryLabel="HG Stage"
        emptyMessage="No HG stage data available."
        horizontal
      />
      <style>{"@keyframes vp-dash-spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}